import { PosState } from "@unleashed-admin/stores/PosSlice";
import { CharityStatus } from "../enums/CharityStatus";
import { CharityType } from "../enums/CharityType";
import { ParticipantType } from "../enums/ParticipantType";
import { Account } from "../features/account/models/Account";
import { AccountParticipant } from "../features/account/models/AccountParticipant";
import {
  GetUpdatedWaiverModel,
  Product,
  PrePurchasedItem,
  Promo,
  CartItem,
  AdhocDiscountViewModel,
  ProductDiscount,
  TicketUpgradeDataModel,
  PurchaseItemViewModel,
} from "../models";
import {
  convertCartItemsToPurchaseItemViewModel,
  convertRedeemedItemsToRedeemItemViewModel,
  convertCartItemsToUpgradeTicketViewModel,
  convertCartItemsToComboItemViewModel,
  convertCartItemsToBundleItemViewModel,
  convertCartItemsToBookingItemViewModel,
  convertAccountToPersonViewModel,
  convertCartItemsToPurchaseItemViewModelToCreateInvoice,
  convertRedeemedItemsToRedeemItemViewModelToCreateInvoice,
  convertCartItemsToComboItemViewModelToCreateInvoice,
  convertCartItemsToBundleItemViewModelToCreateInvoice,
  convertCartItemsToBookingItemViewModelToCreateInvoice,
  convertCartItemsToUpgradeTicketsViewModelToCreateInvoice,
  convertCartItemsToFeeItemViewModel,
  convertCartItemsToProductFeeItemViewModel,
  convertCartItemsToItemFeeViewModelToCreateInvoice,
  convertCartItemsToProductFeeViewModelToCreateInvoice,
} from "./DataConverters";
import { isPurchasedItem } from "./Product";
import { isTicket } from "./Ticket";
import { AdmittedTicketResponse } from "../features/account/models/AdmittedTicketResponse";
import { PointOfSaleCategory } from "../enums/PointOfSaleCategory";
import { isAnnualPass } from "./MembershipAllocationUtilsHelper";

export const getLocatorValue = (text: string): string => {
  if (!text || !text.trim()) return "";

  return text
    .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special characters except spaces
    .trim() // Remove leading and trailing spaces
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .toLowerCase(); // Convert to lower case
};

export const getProductTypeInCart = (
  itemsInCart?: (CartItem | PrePurchasedItem | PurchaseItemViewModel)[]
): PointOfSaleCategory => {
  if (!itemsInCart || itemsInCart.length === 0) {
    return PointOfSaleCategory.Cafe;
  }

  // First priority: Memberships (excluding Annual Pass)
  if (itemsInCart.some(item => item.type === PointOfSaleCategory.Membership)) {
    return PointOfSaleCategory.Membership;
  }

  // Second priority: Annual Pass
  if (itemsInCart.some(item => item.type === PointOfSaleCategory.AnnualPass)) {
    return PointOfSaleCategory.AnnualPass;
  }

  // Default: Cafe
  return PointOfSaleCategory.Cafe;
};

export const getAllProductTypesInCart = (
  itemsInCart?: (CartItem | PrePurchasedItem | PurchaseItemViewModel)[]
): PointOfSaleCategory[] | undefined => 
  !itemsInCart?.length 
    ? undefined 
    : [...new Set(itemsInCart.map(item => item.type as PointOfSaleCategory))];

export const isPartialPaymentEligible = (productTypes?: PointOfSaleCategory[]): boolean => 
  productTypes?.some(p => 
    p === PointOfSaleCategory.Membership) ?? false;

export const getAccountIdForInvoicing = (
  selectedAccountId: number | undefined,
  locationHouseAccountId: number | undefined
): number => {
  return selectedAccountId !== undefined
    ? selectedAccountId
    : locationHouseAccountId !== undefined && locationHouseAccountId > 0
      ? locationHouseAccountId
      : Number(`${import.meta.env.VITE_GLOBAL_HOUSE_ACCOUNT_ID}`);
};

export const formatReservationTime = (time: Date): string => {
  if (!time) return "";

  const date = time instanceof Date ? time : new Date(time);

  return date
    .toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "numeric",
      second: undefined,
      hour12: true,
    })
    .toUpperCase();
};

// Function to deep copy an object
export const deepCopy = <T>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj));
};

export const getUpdatedWaiver = (
  params: GetUpdatedWaiverModel
): AccountParticipant => {
  const updatedWaiver: AccountParticipant = {
    ...params.waiverData,
    accountHolderFirstName: params.waiverData.accountHolderFirstName?.trim(),
    accountHolderLastName: params.waiverData.accountHolderLastName?.trim(),
    waiverParticipants:
      params.waiverData.waiverParticipants
        ?.map((participant: Account) => ({
          ...participant,
          firstName: participant.firstName?.trim(),
          lastName: participant.lastName?.trim(),
          accountHolderId:
            participant.accountHolderId || params.waiverData.accountId,
        }))
        ?.filter(
          (participant) =>
            participant.personType === ParticipantType.CoveredPerson
        ) ?? [],
    admittedTickets:
      params.waiverData.admittedTicketDetails
        ?.flatMap((res: AdmittedTicketResponse) => {
          const ticket = params.products?.find(
            (t: Product) => isTicket(t) && t.ticketId === res.ticketId
          );
          return ticket ? Array(res.quantity).fill(ticket) : [];
        })
        .filter((p): p is Product => !!p) ?? [],
    admittedParticipants: params.waiverData.admittedParticipantIds
      ?.map(
        (participant) =>
          params.waiverData.waiverParticipants &&
          params.waiverData.waiverParticipants
            .filter(
              (participant) =>
                participant.personType === ParticipantType.CoveredPerson
            )
            .find((p: Account) => p.id === participant)
      )
      ?.filter((p): p is Account => !!p),
  };

  // Set Pre-Purchased Passes only for the Primary Account Holder
  if (!params.isLinkedAccount) {
    updatedWaiver.prePurchasedItems = params.waiverData.prePurchasedItems?.map(
      (prePurchasedItem: PrePurchasedItem) => {
        prePurchasedItem = {
          ...prePurchasedItem,
          isPrePurchase: true,
          accountHolderId: params.waiverData.accountId,
          quantity: prePurchasedItem.quantity,
          originalQuantity: deepCopy(prePurchasedItem.quantity),
          isUpgradable:
            params.products?.some(
              (p) =>
                isTicket(p) &&
                p.ticketId === prePurchasedItem.ticketId &&
                p.isUpgradable
            ) ?? false,
        };
        return prePurchasedItem;
      }
    );
  }

  return updatedWaiver;
};

export const stringToBase64 = (str: string) => {
  return btoa(str);
};

export const getPurchasePreviewRequestViewModel = (
  selectedAccount: AccountParticipant,
  locationHouseAccountId: number,
  promoCode: Promo | undefined,
  taxId: string | undefined,
  note: string | undefined,
  itemsInCart: CartItem[],
  itemsForRedemption: PrePurchasedItem[],
  discounts?: AdhocDiscountViewModel[],
  productsDiscount?: ProductDiscount[],
  charityAmount?: number,
  tips?: number,
  checkInParticipants?: Account[],
  ticketUpgradeData?: TicketUpgradeDataModel[],
  isBiMonthlyBilling?: boolean,
) => {
  return {
    accountId: getAccountIdForInvoicing(
      selectedAccount?.accountId,
      locationHouseAccountId
    ),
    couponCode: promoCode?.couponCode,
    taxExemptId: taxId,
    notes: note,
    tips,
    charityDetails:
      charityAmount != undefined
        ? {
          charityAmount: charityAmount,
        }
        : undefined,
    purchaseItems: convertCartItemsToPurchaseItemViewModel(
      itemsInCart.filter((x) => isPurchasedItem(x)) ?? [],
      discounts,
      productsDiscount
    ),
    redeemingItems: convertRedeemedItemsToRedeemItemViewModel(
      selectedAccount || ({} as AccountParticipant),
      itemsForRedemption,
      ticketUpgradeData ?? [],
      discounts,
      productsDiscount
    ),
    upgradedTickets: convertCartItemsToUpgradeTicketViewModel(
      itemsInCart.filter((x) => x.isUpgradedCartTicket),
      ticketUpgradeData,
      discounts,
      productsDiscount
    ),
    comboItems: convertCartItemsToComboItemViewModel(
      itemsInCart.filter((x) => x.isCombo) ?? [],
      discounts,
      productsDiscount
    ),
    bundleItems: convertCartItemsToBundleItemViewModel(
      itemsInCart.filter((x) => x.isBundle) ?? [],
      discounts,
      productsDiscount
    ),
    bookingItem: convertCartItemsToBookingItemViewModel(
      itemsInCart.find((x) => x.isBookingEvent),
      discounts,
      productsDiscount
    ),
    people: convertAccountToPersonViewModel(checkInParticipants ?? []),
    itemFees: convertCartItemsToFeeItemViewModel(
      itemsInCart.filter((x) => x.isItemFee) ?? [],
      discounts,
      productsDiscount,
    ),
    productFees: convertCartItemsToProductFeeItemViewModel(
      itemsInCart.filter((x) => x.isProductFee) ?? [],
    ),
    isBiMonthlyBilling,
  };
};

/* Create PurchasePreviewRequestViewModel for creating Invoice */
export const getPurchasePreviewRequestViewModelToCreateInvoice = (
  selectedAccount: AccountParticipant,
  locationHouseAccountId: number,
  promoCode: Promo | undefined,
  taxId: string | undefined,
  note: string | undefined,
  itemsInCart: CartItem[],
  itemsForRedemption: PrePurchasedItem[],
  discountReasons?: AdhocDiscountViewModel[],
  charityAmount?: number,
  tips?: number,
  checkInParticipants?: Account[],
  ticketUpgradeData?: TicketUpgradeDataModel[],
  supportsFutureEnrollment?: boolean,
  isBiMonthlyBilling?: boolean,
) => {
  // Future enrollment is currently offered only for prepaid memberships (Annual Pass),
  // so the billing start date is taken only from a prepaid membership item.
  const membershipItem = itemsInCart.find(
    (x) =>
      isAnnualPass(x) &&
      !!x.membershipBillingStartDate,
  );

  const membershipBillingStartDate = supportsFutureEnrollment
    ? membershipItem?.membershipBillingStartDate ?? undefined
    : undefined;

  return {
    accountId: getAccountIdForInvoicing(
      selectedAccount?.accountId,
      locationHouseAccountId
    ),
    couponCode: promoCode?.couponCode,
    taxExemptId: taxId,
    notes: note,
    charityDetails:
      charityAmount != undefined
        ? {
          charityAmount: charityAmount,
        }
        : undefined,
    tips,
    membershipBillingStartDate,
    isBiMonthlyBilling,
    purchaseItems: convertCartItemsToPurchaseItemViewModelToCreateInvoice(
      itemsInCart.filter((x) => isPurchasedItem(x)) ?? [],
      discountReasons
    ),
    redeemingItems: convertRedeemedItemsToRedeemItemViewModelToCreateInvoice(
      itemsForRedemption ?? [],
      ticketUpgradeData ?? [],
      discountReasons
    ),
    comboItems: convertCartItemsToComboItemViewModelToCreateInvoice(
      itemsInCart.filter((x) => x.isCombo) ?? [],
      discountReasons
    ),
    bundleItems: convertCartItemsToBundleItemViewModelToCreateInvoice(
      itemsInCart.filter((x) => x.isBundle) ?? [],
      discountReasons
    ),
    people: convertAccountToPersonViewModel(checkInParticipants ?? []),
    bookingItem: convertCartItemsToBookingItemViewModelToCreateInvoice(
      itemsInCart.find((r) => r.isBookingEvent),
      discountReasons
    ),
    upgradedTickets: convertCartItemsToUpgradeTicketsViewModelToCreateInvoice(
      itemsInCart.filter((x) => x.isUpgradedCartTicket) ?? [],
      ticketUpgradeData ?? [],
      discountReasons
    ),
    itemFees: convertCartItemsToItemFeeViewModelToCreateInvoice(itemsInCart.filter((x) => x.isItemFee) ?? [], discountReasons ),
    productFees: convertCartItemsToProductFeeViewModelToCreateInvoice(itemsInCart.filter((x) => x.isProductFee) ?? [] ),
  };
};

export const calculateCharityAmount = (total: number) => {
  const roundUp = Number((Math.ceil(total) - total).toFixed(2));
  return roundUp !== 0 ? roundUp : 1;
};

// This function updates the SelectedAccount's waiverParticipants based on the SecondaryAccounts
export const updateStateWithSecondaryAccounts = (state: PosState) => {
  if (!state.selectedAccount) return;

  // Merge waiver, admitted participants, and memberships from secondary accounts
  if (state.secondaryAccounts.length > 0) {
    const updatedWaiverParticipants =
      state.selectedAccount.waiverParticipants || [];
    const updatedAdmittedParticipants =
      state.selectedAccount.admittedParticipants || [];
    const updatedMembershipSubscriptions =
      state.selectedAccount.membershipSubscriptions || [];

    state.secondaryAccounts.forEach((account: AccountParticipant) => {
      // Add missing waiver participants
      (account.waiverParticipants || []).forEach((participant) => {
        if (!updatedWaiverParticipants.some((p) => p.id === participant.id)) {
          updatedWaiverParticipants.push(participant);
        }
      });

      // Add missing admitted participants
      (account.admittedParticipants || []).forEach((participant) => {
        if (!updatedAdmittedParticipants.some((p) => p.id === participant.id)) {
          updatedAdmittedParticipants.push(participant);
        }
      });

      // Add missing membership subscriptions (deduped by subscriptionId)
      (account.membershipSubscriptions || []).forEach((subscription) => {
        if (
          !updatedMembershipSubscriptions.some(
            (s) => s.subscriptionId === subscription.subscriptionId
          )
        ) {
          updatedMembershipSubscriptions.push(subscription);
        }
      });
    });

    state.selectedAccount = {
      ...state.selectedAccount,
      waiverParticipants: updatedWaiverParticipants,
      admittedParticipants: updatedAdmittedParticipants,
      membershipSubscriptions: updatedMembershipSubscriptions,
    };
  }

  // Build list of all relevant account IDs (selected + secondary)
  const allAccountIds = [
    ...state.secondaryAccounts.map((a) => a.accountId),
    state.selectedAccount.accountId,
  ];

  // Filter waiverParticipants to only those belonging to selected/secondary accounts
  state.selectedAccount.waiverParticipants =
    state.selectedAccount.waiverParticipants?.filter((p) =>
      allAccountIds.includes(p.accountHolderId)
    ) ?? [];

  // Filter membershipSubscriptions to only those whose participant is still in
  // waiverParticipants, unless the subscription itself still requires a waiver.
  // This keeps waiver-required memberships visible after refresh even when the
  // participant has been removed from the waiverParticipants array
  state.selectedAccount.membershipSubscriptions =
    state.selectedAccount.membershipSubscriptions?.filter(
      (s) =>
        s.requiresWaiver ||
        state.selectedAccount?.waiverParticipants?.some(
          (w) => w.id === s.participant.id
        )
    ) ?? [];

  // Filter checkInParticipants to only those in waiverParticipants
  state.checkInParticipants =
    state.checkInParticipants?.filter((p) =>
      state.selectedAccount?.waiverParticipants?.some((w) => w.id === p.id)
    ) ?? [];

  // Filter admittedParticipants to only those in waiverParticipants
  state.selectedAccount.admittedParticipants =
    state.selectedAccount.admittedParticipants?.filter((p) =>
      state.selectedAccount?.waiverParticipants?.some((w) => w.id === p.id)
    ) ?? [];

  // Remove cart items with zero quantity
  state.itemsInCart =
    state.itemsInCart?.filter((item) => item.quantity > 0) ?? [];
};

export const resetCharityData = (state: PosState, initialRender = false) => {
  state.charityDetails = initialRender
    ? undefined
    : {
      ...state.charityDetails,
      status: CharityStatus.None,
      amount: undefined,
      type: CharityType.None,
    };
};
